/**
 * Deterministic mock variants for the PoC.
 * 3 clearly-differentiated profiles so we can validate personalization behavior.
 * All content is illustrative only.
 */

const makeThemes = (accents) =>
  Array.from({ length: 12 }, (_, i) => {
    const n = String(i + 1).padStart(2, "0");
    return {
      id: `t${n}`,
      code: `主题${n}`,
      title: accents[i % accents.length].title,
      summary: accents[i % accents.length].summary,
      strength: accents[i % accents.length].strength, // 0-100
      detail: accents[i % accents.length].detail,
    };
  });

export const MOCK_VARIANTS = [
  {
    id: "A",
    label: "沉稳布局型",
    summaryCards: {
      trait: { title: "核心特质", value: "领袖型 · 沉稳专注", status: "积极" },
      wealth: { title: "财富行动方向", value: "稳健增值 · 结构化布局", status: "上升期" },
      career: { title: "事业发展", value: "跨界整合 · 专业突破", status: "关键期" },
      relationships: { title: "人际关系", value: "贵人相助 · 合作共赢", status: "和谐" },
      growth: { title: "个人成长", value: "认知升级 · 自我重塑", status: "持续" },
      fengshui: { title: "家居与方位", value: "坐北朝南 · 吉位偏东", status: "宜静" },
      numbers: { title: "号码建议", value: "多用 8 · 9 · 13", status: "推荐" },
      priority: { title: "行动优先级", value: "先定战略 · 再整合资源", status: "高优" },
    },
    overviewMetrics: [
      { key: "career", label: "事业", value: 82 },
      { key: "wealth", label: "财富", value: 74 },
      { key: "health", label: "健康", value: 68 },
      { key: "family", label: "家庭", value: 71 },
      { key: "social", label: "人际", value: 79 },
      { key: "growth", label: "成长", value: 86 },
    ],
    themes: makeThemes([
      { title: "自我定位", summary: "适合以专业深度建立影响力。", strength: 82, detail: "建议聚焦一个核心领域，用 12 个月建立体系化输出，避免多线消耗。" },
      { title: "职业节奏", summary: "上半年宜稳，下半年可扩张。", strength: 74, detail: "工作重点从执行逐步转向策略层，保持稳定汇报节奏。" },
      { title: "财富路径", summary: "以结构化组合替代单点押注。", strength: 71, detail: "分层配置：现金流 40% / 成长 35% / 稳健 25%（示意）。" },
      { title: "人际能量", summary: "贵人来自跨行业的老朋友。", strength: 76, detail: "主动维护 3–5 位深度关系，胜过泛化拓展。" },
      { title: "健康节律", summary: "睡眠与心率变异性优先。", strength: 65, detail: "每周 3 次 30 分钟有氧，睡眠 23:00 前。" },
      { title: "学习方向", summary: "系统思维与叙事表达。", strength: 80, detail: "把已有经验结构化，形成可复用模型。" },
    ]),
    numbers: {
      favorable: [1, 3, 6, 8, 9, 13, 18, 21],
      unfavorable: [4, 7, 14, 17],
      usage:
        "手机尾号、常用车牌、门牌等重要号码优先包含多用数字组合。少用号码不必回避到极端，避免情绪化决策即可。",
    },
    wealth: {
      focus: "结构化配置个人资产，建立可持续现金流基线。",
      priority: [
        "梳理未来 6 个月固定开支与被动收入缺口",
        "建立不超过 3 类核心资产的分层配置表",
        "每月固定复盘一次，避免情绪交易",
      ],
      watch: [
        "过度乐观带来的仓位膨胀",
        "对高波动新趋势的追随冲动",
        "亲友高热度建议的从众效应",
      ],
      next: [
        "Q1：确认基线预算与紧急金 (6 个月生活费)",
        "Q2：构建长期定投组合并落地文档化流程",
        "Q3：评估副业/技能变现路径并选定 1 条主线",
        "Q4：全年复盘与下年度目标校准",
      ],
    },
  },
  {
    id: "B",
    label: "探索突破型",
    summaryCards: {
      trait: { title: "核心特质", value: "开拓型 · 直觉敏锐", status: "活跃" },
      wealth: { title: "财富行动方向", value: "机会型进取 · 分批建仓", status: "扩张期" },
      career: { title: "事业发展", value: "创新试点 · 快速迭代", status: "上升" },
      relationships: { title: "人际关系", value: "跨圈层拓展 · 主动破圈", status: "扩展" },
      growth: { title: "个人成长", value: "多元输入 · 快速内化", status: "加速" },
      fengshui: { title: "家居与方位", value: "坐西朝东 · 吉位偏南", status: "宜动" },
      numbers: { title: "号码建议", value: "多用 2 · 5 · 16", status: "推荐" },
      priority: { title: "行动优先级", value: "先小步验证 · 再规模化", status: "关键" },
    },
    overviewMetrics: [
      { key: "career", label: "事业", value: 88 },
      { key: "wealth", label: "财富", value: 66 },
      { key: "health", label: "健康", value: 62 },
      { key: "family", label: "家庭", value: 60 },
      { key: "social", label: "人际", value: 84 },
      { key: "growth", label: "成长", value: 90 },
    ],
    themes: makeThemes([
      { title: "机会识别", summary: "对新兴信号具备敏锐嗅觉。", strength: 88, detail: "建议以 2 周为周期做小实验，控制单次试错成本。" },
      { title: "行动节奏", summary: "快速验证 → 复盘 → 规模化。", strength: 82, detail: "把每次尝试固化为可复用的模板，避免重复造轮子。" },
      { title: "跨界连接", summary: "把不同行业的经验组合成方案。", strength: 85, detail: "定期参加 1 场跨领域交流，输出复盘笔记。" },
      { title: "资金弹性", summary: "保留 30% 机会仓，避免全仓被动。", strength: 68, detail: "机会仓分 3 批入场，任一批亏 20% 停手复盘。" },
      { title: "精力管理", summary: "创造力与恢复力需并行。", strength: 60, detail: "把重要决策放在上午，晚间进行低强度输入。" },
      { title: "叙事表达", summary: "让复杂想法可被理解。", strength: 78, detail: "用一句话讲清楚正在做的事，再补支撑要点。" },
    ]),
    numbers: {
      favorable: [2, 5, 11, 16, 19, 22],
      unfavorable: [4, 8, 13],
      usage:
        "在需要新机会推进的号码（如工作电话、项目编号）中优先使用多用数字。少用号码不建议出现在核心识别号（身份关键号）里。",
    },
    wealth: {
      focus: "以小步验证的方式捕捉机会，同时保护主账户安全边际。",
      priority: [
        "设定不可动用的安全底仓 (至少 12 个月生活费)",
        "把可承受损失金额上限固化为规则",
        "每次尝试都写下 30 天复盘 + 淘汰机制",
      ],
      watch: [
        "多线并行导致注意力稀释",
        "情绪高点加仓、低点砍仓的模式",
        "对权威建议的过度依赖",
      ],
      next: [
        "Q1：建立试点清单并筛出 2 条主线",
        "Q2：把 1 条主线做到可复现的最小闭环",
        "Q3：验证结果后再决定是否扩张投入",
        "Q4：形成个人机会漏斗与复盘制度",
      ],
    },
  },
  {
    id: "C",
    label: "整合守护型",
    summaryCards: {
      trait: { title: "核心特质", value: "共情型 · 系统协调", status: "温和" },
      wealth: { title: "财富行动方向", value: "长期主义 · 现金流优先", status: "稳态" },
      career: { title: "事业发展", value: "组织提效 · 团队协同", status: "稳步" },
      relationships: { title: "人际关系", value: "深耕核心圈 · 家庭优先", status: "稳固" },
      growth: { title: "个人成长", value: "沉淀经验 · 系统化输出", status: "厚积" },
      fengshui: { title: "家居与方位", value: "坐东朝西 · 吉位偏北", status: "宜整" },
      numbers: { title: "号码建议", value: "多用 3 · 6 · 24", status: "推荐" },
      priority: { title: "行动优先级", value: "先稳后进 · 系统优先", status: "稳健" },
    },
    overviewMetrics: [
      { key: "career", label: "事业", value: 72 },
      { key: "wealth", label: "财富", value: 78 },
      { key: "health", label: "健康", value: 76 },
      { key: "family", label: "家庭", value: 88 },
      { key: "social", label: "人际", value: 70 },
      { key: "growth", label: "成长", value: 74 },
    ],
    themes: makeThemes([
      { title: "关系底盘", summary: "家庭与核心关系是长期资源。", strength: 88, detail: "把重要关系写进日历，主动创造在场感与陪伴质量。" },
      { title: "现金流基础", summary: "先稳定，再谈成长。", strength: 80, detail: "把主动收入拆成 3 层：稳定、增长、可选。" },
      { title: "工作系统", summary: "用流程降低不确定性。", strength: 74, detail: "把重复决策 SOP 化，把注意力留给关键选择。" },
      { title: "身心健康", summary: "长期主义的隐形本金。", strength: 76, detail: "每周固定 2 次运动 + 1 次深度休息。" },
      { title: "长期资产", summary: "耐心是最贵的复利。", strength: 78, detail: "10 年视角配置：ETF / 保障 / 现金 分层持有。" },
      { title: "自我表达", summary: "把内在秩序变成外在作品。", strength: 68, detail: "每月输出 1 篇个人思考，练习结构化表达。" },
    ]),
    numbers: {
      favorable: [3, 6, 12, 15, 24, 27],
      unfavorable: [4, 9, 17, 22],
      usage:
        "在长期使用的号码（家庭电话、常用银行卡尾号）中优先出现多用数字。少用号码不必刻意更换，重点关注决策不要被数字情绪牵引。",
    },
    wealth: {
      focus: "以家庭与长期现金流为核心，构建稳定可持续的财务底盘。",
      priority: [
        "把家庭年度支出与保障缺口写清楚",
        "把长期投资比例做成自动化定投",
        "每季度做一次家庭财务对齐会议",
      ],
      watch: [
        "为他人担责导致个人现金流失衡",
        "对高波动产品的间接暴露",
        "过度储蓄导致长期购买力下降",
      ],
      next: [
        "Q1：完成家庭资产负债一页纸",
        "Q2：确认保障 (医疗/意外/寿) 是否闭环",
        "Q3：搭建 10 年长期账户与定投计划",
        "Q4：家庭年度复盘与目标共识",
      ],
    },
  },
];
